export const defaultSettings = {
  adminEmail: '',
  gym: {
    name: '',
    tagline: '',
    address: '',
    phone: '',
    website: '',
    openingHours: '',
    timezone: 'Asia/Karachi',
  },
  logo: null,
  logoPreview: null,
  theme: 'dark',
  /** Next CR. No. / Invoice ID floor (first digital = paper book CR 36128). */
  invoiceSequenceStart: 36128,
  currency: {
    code: 'PKR',
    symbol: 'Rs',
    position: 'before',
    decimalPlaces: 0,
  },
  email: {
    enabled: true,
    host: 'smtp.gmail.com',
    port: '587',
    encryption: 'tls',
    username: '',
    password: '',
    fromName: '',
    fromEmail: '',
  },
};

export const currencyOptions = [
  { code: 'PKR', symbol: 'Rs', label: 'Pakistani Rupee (PKR)', country: 'Pakistan' },
  { code: 'USD', symbol: '$', label: 'US Dollar (USD)', country: 'United States' },
  { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar (CAD)', country: 'Canada' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar (AUD)', country: 'Australia' },
  { code: 'EUR', symbol: '€', label: 'Euro (EUR)', country: 'Germany' },
  { code: 'GBP', symbol: '£', label: 'British Pound (GBP)', country: 'United Kingdom' },
  { code: 'INR', symbol: '₹', label: 'Indian Rupee (INR)', country: 'India' },
  { code: 'AED', symbol: 'د.إ', label: 'UAE Dirham (AED)', country: 'United Arab Emirates' },
  { code: 'SAR', symbol: '﷼', label: 'Saudi Riyal (SAR)', country: 'Saudi Arabia' },
  { code: 'CHF', symbol: 'CHF', label: 'Swiss Franc (CHF)', country: 'Switzerland' },
];

export const timezoneOptions = [
  'Asia/Karachi',
  'Asia/Dubai',
  'UTC',
  'America/New_York',
  'Europe/London',
];

export const themeOptions = [
  { value: 'dark', label: 'Dark Theme' },
  { value: 'light', label: 'Light Theme' },
];

const ADMIN_EMAIL_KEY = 'gym-admin-email';

function resolveGymId(gymId) {
  if (gymId) return gymId;
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem('gym-current-id');
}

function settingsStorageKey(gymId) {
  const id = resolveGymId(gymId);
  return id ? `gym-admin-settings-${id}` : 'gym-admin-settings';
}

function emailStorageKey(gymId) {
  const id = resolveGymId(gymId);
  return id ? `gym-admin-email-${id}` : ADMIN_EMAIL_KEY;
}

function readStorage(key) {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
  } catch {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }
}

function logoStorageKey(gymId) {
  const id = resolveGymId(gymId);
  return id ? `gym-admin-logo-${id}` : 'gym-admin-logo';
}

export function loadAdminLogo(gymId) {
  try {
    return readStorage(logoStorageKey(gymId)) || null;
  } catch {
    return null;
  }
}

export function saveAdminLogo(logoPreview, gymId) {
  try {
    const key = logoStorageKey(gymId);
    if (logoPreview) writeStorage(key, logoPreview);
    else {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

export function loadAdminSettings(gymId) {
  try {
    const raw = readStorage(settingsStorageKey(gymId));
    const logoPreview = loadAdminLogo(gymId);
    if (raw) {
      const saved = JSON.parse(raw);
      const invoiceSequenceStart = Number(saved.invoiceSequenceStart);
      return {
        ...defaultSettings,
        ...saved,
        gym: { ...defaultSettings.gym, ...saved.gym },
        currency: { ...defaultSettings.currency, ...saved.currency },
        email: { ...defaultSettings.email, ...saved.email },
        invoiceSequenceStart: Number.isFinite(invoiceSequenceStart) && invoiceSequenceStart > 0
          ? Math.floor(invoiceSequenceStart)
          : defaultSettings.invoiceSequenceStart,
        // Prefer dedicated logo key so large images don't get dropped from settings JSON
        logoPreview: logoPreview || saved.logoPreview || null,
      };
    }
    if (logoPreview) {
      return {
        ...defaultSettings,
        gym: { ...defaultSettings.gym },
        currency: { ...defaultSettings.currency },
        email: { ...defaultSettings.email },
        logoPreview,
      };
    }
  } catch {
    // ignore invalid storage
  }
  return {
    ...defaultSettings,
    gym: { ...defaultSettings.gym },
    currency: { ...defaultSettings.currency },
    email: { ...defaultSettings.email },
  };
}

export function saveAdminSettings(settings, gymId) {
  try {
    // Keep logo out of the main JSON blob to avoid quota wipeouts
    saveAdminLogo(settings.logoPreview || null, gymId);
    const invoiceSequenceStart = Number(settings.invoiceSequenceStart);
    const payload = {
      gym: settings.gym,
      currency: {
        ...settings.currency,
        decimalPlaces: Number(settings.currency?.decimalPlaces ?? 0),
      },
      theme: settings.theme,
      adminEmail: settings.adminEmail || '',
      invoiceSequenceStart: Number.isFinite(invoiceSequenceStart) && invoiceSequenceStart > 0
        ? Math.floor(invoiceSequenceStart)
        : defaultSettings.invoiceSequenceStart,
      email: {
        ...defaultSettings.email,
        ...settings.email,
      },
    };
    writeStorage(settingsStorageKey(gymId), JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent('gym-admin-settings-updated', {
      detail: {
        gymId: resolveGymId(gymId),
        settings: { ...payload, logoPreview: settings.logoPreview || null },
      },
    }));
  } catch {
    // ignore storage errors
  }
}

/**
 * First gym admin signup: lock sender (From) email to their registration address.
 * Does not overwrite if a sender was already set for this gym.
 */
export function ensureGymAdminSenderEmail(email, gymName, gymId) {
  const trimmed = String(email || '').trim();
  if (!trimmed) return loadAdminSettings(gymId);

  setAdminSignupEmail(trimmed, gymId);
  const settings = loadAdminSettings(gymId);
  const alreadySet = String(settings.email?.fromEmail || settings.adminEmail || '').trim();

  // Only configure automatically the first time for this gym
  if (alreadySet) {
    return settings;
  }

  const fromName = (gymName || settings.gym?.name || 'Gym').trim() || 'Gym Notifications';
  const next = {
    ...settings,
    adminEmail: trimmed,
    gym: {
      ...settings.gym,
      name: gymName || settings.gym?.name || '',
    },
    email: {
      ...defaultSettings.email,
      ...settings.email,
      username: trimmed,
      fromEmail: trimmed,
      fromName,
      enabled: true,
    },
  };
  saveAdminSettings(next, gymId);
  return next;
}

/** Resolved sender email for a gym (first admin email). */
export function getGymSenderEmail(gymId) {
  const settings = loadAdminSettings(gymId);
  return (
    String(settings.email?.fromEmail || '').trim()
    || String(settings.adminEmail || '').trim()
    || getAdminSignupEmail(gymId)
    || ''
  );
}

export function getCountryForCurrency(code) {
  const match = currencyOptions.find((c) => c.code === code);
  return match?.country || 'Pakistan';
}

export function getDefaultMemberCountry() {
  const settings = loadAdminSettings();
  return getCountryForCurrency(settings.currency.code);
}

export function getAdminSignupEmail(gymId) {
  try {
    return readStorage(emailStorageKey(gymId)) || defaultSettings.adminEmail;
  } catch {
    return defaultSettings.adminEmail;
  }
}

export function setAdminSignupEmail(email, gymId) {
  try {
    const id = resolveGymId(gymId);
    if (id) writeStorage(emailStorageKey(id), email);
  } catch {
    // ignore storage errors
  }
}
